import { convertDateUnitType } from '@/coreDomainsModule/date';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { RegisterBusinessRepository } from '@/coreInfrastructureModule/Repositories/RegisterBusinessRepository';
import {
  PostBusinessInfo,
  PostRegisterBusinessResponseResult,
  RegisterBusinessPostRequest,
} from '@/coreInfrastructureModule/axios/generated/registerBusinessManage/api';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';

/**
 * エクセル取り込み？のビジネスロジック
 */
export class PutRegisterBusinessUseCase {
  /**
   * NOTE: エクセル取り込みを弄りたくないので、既存の登録ロジックを流用している
   * @param repository - 取引情報の永続化の責務を負うクラス
   * @param registeredNotAggregateBusiness - 集計されていないビジネス
   * @param registeredAggregateBusiness - 集計されたビジネス
   * @param executionDate - 実行日
   * @param useUpsert - 更新か新規登録か
   */
  public Execute = async (
    repository: RegisterBusinessRepository,
    registeredNotAggregateBusiness: IBusiness[],
    registeredAggregateBusiness: IBusiness[],
    executionDate: Date,
    useUpsert: boolean
  ): Promise<INetworkResult<PostRegisterBusinessResponseResult, IError>> => {
    const req: RegisterBusinessPostRequest = {
      useUpsert: useUpsert,
      postAggregateBusinessInfo: [],
    };

    registeredNotAggregateBusiness.forEach((business) => {
      const putItem: PostBusinessInfo = {
        warehouse: business.warehouse,
        warehouseNumber: business.warehouseNumber,
        package: business.package,
        businessNumber: business.businessNumber,
        supplierName: business.supplierName,
        supplierNumber: business.supplierNumber,
        isAggregateBusiness: false,
        businessName: business?.businessName ?? '',
        unit: business.unit ?? '',
        leadTime: business.leadTime?.toString() ?? '',
        leadTimeUnit: business.leadTimeUnit ?? '',
        inTransitDays: business.inTransitDays ?? 0,
        moq: business.MOQ,
        orderPackage: business.orderPackage?.toString(),
        stdInventoryIndices: business?.stdInventoryIndices ?? {},
        userCustomParameters: business?.businessMemo ?? {},
        arrivals: [],
        mvpInventory: [],
        executionDate: DateService.formatDateToISOString(executionDate), // iso8601形式に変換
        customerName: business.customerName ?? '',
        customerNumber: business.customerNumber ?? '',
        salesDestinationName: business.salesDestinationName ?? '',
        salesDestinationNumber: business.salesDestinationNumber ?? '',
        salesResults: [],
        customerAnnounce: [],
        customerAnnounceDaily: [],
      };
      business?.salesResults?.forEach((salesResult) => {
        putItem.salesResults.push({
          year: salesResult.year,
          month: salesResult.month,
          amount: salesResult.amount.roundToDecimalPlacesNumber(6),
        });
      });
      business?.customerAnnounce?.forEach((customerAnnounce) => {
        putItem.customerAnnounce.push({
          manageInitiatedYearMonth: customerAnnounce.manageInitiatedYearMonth,
          year: customerAnnounce.year,
          month: customerAnnounce.month,
          amount: customerAnnounce.amount.roundToDecimalPlacesNumber(6),
        });
      });
      business?.customerAnnounceDaily?.forEach((customerAnnounce) => {
        putItem.customerAnnounceDaily?.push({
          manageInitiatedYearMonth: customerAnnounce.manageInitiatedYearMonth,
          year: customerAnnounce.year,
          month: customerAnnounce.month,
          day: customerAnnounce.day,
          amount: customerAnnounce.amount.roundToDecimalPlacesNumber(6),
        });
      });
      req.postAggregateBusinessInfo.push(putItem);
    });

    registeredAggregateBusiness.forEach((aggregateBusiness) => {
      const putItem: PostBusinessInfo = {
        warehouse: aggregateBusiness.warehouse,
        warehouseNumber: aggregateBusiness.warehouseNumber,
        package: aggregateBusiness.package,
        businessNumber: aggregateBusiness.businessNumber,
        supplierName: aggregateBusiness.supplierName,
        supplierNumber: aggregateBusiness.supplierNumber,
        isAggregateBusiness: true,
        businessName: aggregateBusiness.businessName ?? '',
        unit: aggregateBusiness.unit ?? '',
        leadTime: aggregateBusiness.leadTime?.toString() ?? '',
        leadTimeUnit: convertDateUnitType(aggregateBusiness.leadTimeUnit ?? ''),
        inTransitDays: aggregateBusiness.inTransitDays ?? 0,
        moq: aggregateBusiness.MOQ,
        orderPackage: aggregateBusiness.orderPackage?.toString(),
        stdInventoryIndices: aggregateBusiness?.stdInventoryIndices ?? {},
        arrivals: [],
        mvpInventory: [],
        executionDate: DateService.formatDateToISOString(executionDate), // iso8601形式に変換
        customerName: aggregateBusiness.customerName ?? '',
        customerNumber: aggregateBusiness.customerNumber ?? '',
        salesDestinationName: aggregateBusiness.salesDestinationName ?? '',
        salesDestinationNumber: aggregateBusiness.salesDestinationNumber ?? '',
        salesResults: [],
        customerAnnounce: [],
        customerAnnounceDaily: [],
        userCustomParameters: aggregateBusiness.businessMemo ?? {},
      };
      aggregateBusiness?.arrivals?.forEach((arrival) => {
        putItem.arrivals.push({
          year: arrival.year,
          month: arrival.month,
          amount: arrival.amount.roundToDecimalPlacesNumber(6),
        });
      });
      aggregateBusiness?.mvpInventory?.forEach((mvpInventory) => {
        putItem.mvpInventory.push({
          year: mvpInventory.year,
          month: mvpInventory.month,
          amount: mvpInventory.amount.roundToDecimalPlacesNumber(6),
        });
      });

      // 標準在庫指数の単位変換
      if (putItem.stdInventoryIndices['標準在庫指数'] && putItem.stdInventoryIndices['標準在庫指数'].option.unit) {
        putItem.stdInventoryIndices['標準在庫指数'].option.unit = convertDateUnitType(
          putItem.stdInventoryIndices['標準在庫指数'].option.unit
        );
      }

      req.postAggregateBusinessInfo.push(putItem);
    });

    try {
      const result = await repository.put(req);
      switch (result.status.type) {
        case 'success':
          console.log('取引登録件数', result.status.data.registeredBusinessCounts);
          break;
        case 'error':
          throw result.status.error;
        default:
          throw Error('想定外のエラー');
      }

      return {
        status: {
          type: 'success',
          data: result.status.data,
        },
      };
    } catch (error) {
      console.error('error', error); // 基本的にはここには来ない
      return {
        status: {
          type: 'error',
          error: error as IError,
        },
      };
    }
  };
}
